import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useService } from "../contexts/ServiceContext";
import { requiresAuth, LOGIN_ROUTE } from "../config/routes";

interface RouteGuardProps {
  children: React.ReactNode;
  path: string;
}

const RouteGuard: React.FC<RouteGuardProps> = ({ children, path }) => {
  const { isAuthenticated, loading } = useAuth();
  const { services } = useService();

  const location = useLocation();

  // 如果正在加载认证状态，显示加载状态
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div>正在验证身份...</div>
      </div>
    );
  }

  // 对于 catch-all 路由（path="*"），使用实际路径进行检查
  const actualPath = path === "*" ? location.pathname : path;

  // 检查是否为系统服务路由，系统服务可能有特殊的认证处理
  const service = services.find(
    (s) => s.routePath === actualPath || `/${s.name}` === actualPath
  );
  if (service && service.isSystemService) {
    return <>{children}</>;
  }

  // 对于动态路由（微服务路由），检查服务列表
  // 如果路径匹配某个服务的路由路径，则允许访问（已通过系统服务检查）
  const matchedService = services.find(
    (s) => s.routePath === actualPath || `/${s.name}` === actualPath
  );
  if (matchedService) {
    // 如果是已认证用户访问服务路由，允许访问
    if (isAuthenticated) {
      return <>{children}</>;
    }
  }

  // 对于 catch-all 路由，如果是已认证用户，允许通过（ContainerPage 会处理路径识别）
  if (path === "*" && isAuthenticated) {
    return <>{children}</>;
  }

  // 简化认证检查，暂时禁用动态路由检查以避免无限重渲染
  const needsAuth = requiresAuth(actualPath);

  // 如果需要认证但用户未登录，重定向到登录页
  if (needsAuth && !isAuthenticated) {
    return <Navigate to={LOGIN_ROUTE} state={{ from: location }} replace />;
  }

  // 如果用户已登录但访问登录页，重定向到主页面
  if (actualPath === LOGIN_ROUTE && isAuthenticated) {
    return <Navigate to="/services" replace />;
  }

  // 其他情况正常渲染
  return <>{children}</>;
};

export default RouteGuard;
